// v2 design tokens — ported from the prototype's css/styles.css `:root` so the
// React app matches Ben's design (neutral-dark, green accent, Instrument Sans +
// Barlow Condensed). Kept SEPARATE from v1's lib/theme.ts (navy) — v2 only.
//
// Key names mirror v1's theme object so v2 components only had to swap the import
// path, not every usage. Fonts load via a Google Fonts <link> in index.html
// (self-hosting is a pre-prod hardening item — see docs/frontend-v2/deferred.md).
export const theme = {
  // Surfaces (prototype --bg-color / --panel-bg / --block-color / --bg-elevated)
  page: "#121212",
  tile: "#1a1a1a", // a widget/panel
  tileHeader: "#222222", // slightly elevated header strip
  surface: "#242424", // controls / chips
  input: "#1a1a1a",
  field: "#1a1a1a",
  // Lines
  border: "#333333",
  borderInput: "#444444",
  borderSubtle: "#2a2a2a", // prototype --border-subtle (row dividers inside cards)
  // Canvas grid lines — the prototype draws the workspace grid in solid #1f1f1f
  // (its --bg-mid) on the #121212 ground; v2 had near-invisible faint lines.
  grid: "#1f1f1f",
  // Text (--text-color / --text-secondary / --text-muted / --text-dim)
  ink: "#ffffff",
  inkMuted: "#cccccc",
  inkFaint: "#888888",
  inkDim: "#555555",
  // Accent — the prototype's green
  accent: "#2ecc71",
  accentSurface: "rgba(46, 204, 113, 0.15)",
  // Soft accent — the prototype's --accent-color (rgba .4). It uses this for the
  // FILL/glow of a valid-placement indicator and keeps a solid #2ecc71 border on
  // top; v2 had used the solid green for the glow too, which read harsher.
  accentSoft: "rgba(46, 204, 113, 0.4)",
  danger: "#e74c3c",
  dangerSoft: "rgba(231, 76, 60, 0.4)", // prototype .snap-indicator.invalid fill
  // Merge = electric blue, deliberately distinct from the green "valid placement"
  // (prototype .snap-indicator.merge): dropping here COMBINES into a comparison,
  // it isn't just a place. Green stays for placement/replace, red for invalid.
  merge: "#00c2ff",
  mergeSoft: "rgba(0, 194, 255, 0.4)",
  // Elevation (prototype --shadow-md / --shadow-lg).
  shadowMd: "rgba(0, 0, 0, 0.5)",
  shadowLg: "rgba(0, 0, 0, 0.8)",
  // Widget frame — the prototype's .placed-module / .module-header (css/styles.css).
  // A subtle top-lit gradient panel (not a flat fill), a slightly lighter border
  // than the app border, a translucent header strip, and a #666 resize bracket.
  moduleBg: "linear-gradient(145deg, #333333 0%, #1a1a1a 100%)",
  moduleBorder: "#353535",
  headerBg: "rgba(255, 255, 255, 0.02)",
  headerBorder: "#222222",
  resizeBracket: "#666666",
  // Shape
  radius: 12,
  // Fonts. The prototype is Instrument Sans EVERYWHERE — names, numbers, stat
  // values, titles all inherit the body font; Barlow Condensed appears only on a
  // welcome-screen byline v2 doesn't have. So widget content uses `fontBody`;
  // `fontDisplay` (Barlow) is kept only for the app wordmark's brand look.
  fontBody: "'Instrument Sans', system-ui, -apple-system, sans-serif",
  fontDisplay: "'Barlow Condensed', 'Instrument Sans', system-ui, sans-serif",
  // Type scale — the prototype's --t-* tokens (rem → px at the 16px root).
  fontSize: {
    micro: 8, // --t-micro 0.5rem
    label: 10, // --t-label 0.6rem
    data: 11, // --t-data 0.7rem
    body: 11, // --t-body 0.7rem
    title: 16, // --t-title 1rem
    heading: 20, // --t-heading 1.25rem
    hero: 28, // --t-hero 1.75rem
  },
} as const;
